// @ts-strict-ignore
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { DrillingHoleInfo } from '@3d-scene/model3d/util/drillinghole/drilling-hole-info';
import { Line } from '@3d-scene/model/line';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '../../../container.service';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { DrillingHoleUtils } from '@3d-scene/model3d/util/drillinghole/drilling-hole-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { OnRailReferenceArticleLocationVO } from '@project/components/article/model/vo/on-rail-reference-article-location.vo';
import { RangeOnLineConnectionTypeDef } from '@project/constants/range-on-line-connection-type-def';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { CollisionHelper } from '@3d-scene/helper/collision.helper';
import { MetricUtils } from '@3d-scene/common/util/metric-utils';
import { Plane, Vector3 } from 'three';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { UndoReplaceArticleOperation } from '@shared/undo/operation/undo-replace-article-operation';
import { Face3D } from '@3d-scene/model/face-3d';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { Injectable } from '@angular/core';
import { ArticleContainerUtils } from '../../article-container-utils';
import { DrillingHoleService } from '@project/actions/drilling-hole/drilling-hole.service';
import { InsertedArticleData } from '@project/components/article/processor/i-article-location.processor';
import { InsertArticleService } from '@project/components/article/service/insert-article.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ProServeProjectImportProcessor } from '@project/components/import-export/processor/pro-serve-project-import.processor';
import { MiniTerminalBlocksProjectCreationServiceDelegator } from '@configuration/wizard/service/mini-terminal-blocks-project-creation-service.delegator';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';

@Injectable({ providedIn: 'root' })
export class CreateDrillingHolePresenter {

  constructor(
    private projectModel: ProjectModel,
    private detachArticleUsageService: DetachArticleUsageService,
    private undoManager: UndoRedoManager,
    private localeResourceManager: LocaleResourceManager,
    private proServeProjectImportProcessor: ProServeProjectImportProcessor,
    private miniTerminalBlocksProjectCreationServiceDelegator: MiniTerminalBlocksProjectCreationServiceDelegator,
    private undoRedoUtils: UndoRedoUtils,
    private containerService: ContainerService,
    private articleContainerUtils: ArticleContainerUtils,
    private _insertArticleService: InsertArticleService,
    private drillingHoleService: DrillingHoleService
  ) {}

  public async createDrillingHoles(drillingHoleInfos: DrillingHoleInfo[],
                                   parentUsage: ArticleUsageVO,
                                   drillingHoleArticles: ArticleVO[],
                                   drillingHoleGUIDsToDetach: string[],
                                   baseLine: Line,
                                   isPresetCreation: boolean = false,
                                   pushUndoOperations: boolean = true): Promise<void> {
    if (drillingHoleGUIDsToDetach) {
      let childGUIDs: string[] = [];
      for (const detachUsageId of drillingHoleGUIDsToDetach) {
        const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(detachUsageId);
        childGUIDs = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
        for (const childGUID of childGUIDs) {
          drillingHoleGUIDsToDetach.push(childGUID);
        }
      }
    }

    let detachOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds(drillingHoleGUIDsToDetach);
    if (isPresetCreation && !pushUndoOperations) {
      detachOperation = null;
    }
    await this.detachExistingDrillingHoles(drillingHoleGUIDsToDetach);
    if (drillingHoleInfos.length > 0) {
      const parentContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(parentUsage.id);
      const articleHeight: number = this.calculateArticleHeight(parentContainer, baseLine) + 3 * CommonConstants.EPSILON;
      this.projectModel.lastDrillingHoleLength = articleHeight;
    }

    const createdArticleGuids: string[] = [];

    for (const drillingHoleInfo of drillingHoleInfos) {
      const articleCreationData = await this.createDrillingHole(drillingHoleInfo, parentUsage, drillingHoleArticles, isPresetCreation, pushUndoOperations);
      createdArticleGuids.push(...articleCreationData.insertedArticleGUIDs);
    }
    await this.completeDrillingHolesCreation(detachOperation, createdArticleGuids, pushUndoOperations);

    if (drillingHoleInfos.length > 0) {
      if (!isPresetCreation) {
        await this.proServeProjectImportProcessor.drillingHoleLoaded();
      }
    } else {
      await this.proServeProjectImportProcessor.drillingHoleNOTLoaded();
    }
  }

  private async detachExistingDrillingHoles(drillingHoleGUIDs: string[]): Promise<void> {
    await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(drillingHoleGUIDs);
  }

  private async createDrillingHole(drillingHoleInfo: DrillingHoleInfo,
                                   parentUsage: ArticleUsageVO,
                                   drillingHoleArticles: ArticleVO[],
                                   isPresetCreation: boolean,
                                   pushUndoOperations: boolean): Promise<InsertedArticleData> {
    const drillingHoleArticle: ArticleVO = DrillingHoleUtils.findArticleByDrillingHole(drillingHoleInfo, drillingHoleArticles);
    if (drillingHoleArticle) {
      if (NumberUtils.isNotNumber(drillingHoleInfo.positionOffset)) {
        drillingHoleInfo.positionOffset = 0;
      }
      const drillingHoleOffset: number = drillingHoleInfo.position + drillingHoleInfo.positionOffset - drillingHoleInfo.size.sizeOnLine / 2;
      const onRailReference: OnRailReferenceArticleLocationVO = new OnRailReferenceArticleLocationVO();
      onRailReference.articleOffset = drillingHoleOffset;
      onRailReference.rangeOnLineArticleUsage = parentUsage;
      onRailReference.rangeOnLineType = RangeOnLineConnectionTypeDef.TYPE_DRILLING_HOLE;
      this.projectModel.lastDrillingHoleOffset = drillingHoleInfo.position;

      let messageType: string;
      if (!isPresetCreation) {
        if (pushUndoOperations) {
          messageType = 'insertUndoableDrillingHoleArticle';
        } else {
          messageType = 'insertDrillingHoleArticle';
        }
      } else if (pushUndoOperations) {
        messageType = 'insertUndoablePresetDrillingHoleArticle';
      } else {
        messageType = 'insertPresetDrillingHoleArticle';
      }

      const insertedData: InsertedArticleData = await this._insertArticleService.insertArticleByArticleLocation(drillingHoleArticle, onRailReference, messageType);

      if (!insertedData) {
        await this.proServeProjectImportProcessor.drillingHoleNOTLoaded();
      }

      return insertedData;

    } else {
      await this.proServeProjectImportProcessor.drillingHoleNOTLoaded();
      throw new Error(this.localeResourceManager.getValueByKey('createDrillingHolePresenter.message.drilling_hole_article_not_found'));
    }
  }

  private calculateArticleHeight(article: ArticleContainer, line: Line): number {
    const plane: Plane = new Plane();
    plane.setFromCoplanarPoints(line.start, line.end, new Vector3(line.start.x, CommonConstants.CONSTANT_OFFSET, line.start.z));
    plane.normalize();

    const intersectFaces: Face3D[] = CollisionHelper.findIntersectionFacesByPlane(article, plane, this.articleContainerUtils);
    const min: Vector3 = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    const max: Vector3 = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
    MetricUtils.findMinMaxForFaces(intersectFaces, min, max);

    return max.y - min.y;
  }

  private pushUndoDrillingHolesOperations(detachOperation: UndoDetachArticlesOperation, createdDrillingHoleArticleGUIDs: string[], pushUndoOperations: boolean): void {
    let attachOperation: UndoAttachArticlesOperation = null;
    if (pushUndoOperations) {
      attachOperation = this.undoRedoUtils.createUndoAttachOperationByUsageIds(createdDrillingHoleArticleGUIDs);
    }
    if (detachOperation || attachOperation) {
      this.undoManager.pushUndo(new UndoReplaceArticleOperation(attachOperation, detachOperation));
    }
  }

  private async completeDrillingHolesCreation(detachOperation: UndoDetachArticlesOperation, createdDrillingHoleArticleGUIDs: string[], pushUndoOperations: boolean): Promise<void> {
    this.pushUndoDrillingHolesOperations(detachOperation, createdDrillingHoleArticleGUIDs, pushUndoOperations);
    await this.dispatchInsertSuccessEvent();
  }

  private async dispatchInsertSuccessEvent(): Promise<void> {
    this.drillingHoleService.notifyDrillingHolesInserted();
    await this.miniTerminalBlocksProjectCreationServiceDelegator.mountingRailAttached();
    this.drillingHoleService.notifyPresetCompleted(true);
  }
}
